import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FileText,
  Upload,
  Users,
  UserCheck,
  Search,
  Trophy,
  Wheat,
  Rocket,
} from 'lucide-react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';

const Nominations = () => {
  const { t } = useTranslation('nominations');
  const location = useLocation();

  // Handle scroll to section when component loads with hash
  useEffect(() => {
    if (location.hash === '#how-to-enter') {
      setTimeout(() => {
        const element = document.getElementById('how-to-enter');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  // Icon mapping for steps
  const stepIconMap = {
    FileText,
    Upload,
    Search,
    UserCheck,
    Trophy
  };

  // Get steps with icons
  const stepsWithIcons = [];
  for (let i = 0; i < 5; i++) {
    const step = {
      number: t(`steps.list.${i}.number`),
      title: t(`steps.list.${i}.title`),
      details: t(`steps.list.${i}.details`),
      icon: stepIconMap[['FileText', 'Upload', 'Search', 'UserCheck', 'Trophy'][i]]
    };
    stepsWithIcons.push(step);
  }

  return (
    <Box sx={{ backgroundColor: '#FFFFFF' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        pt: { xs: "160px", lg: "120px" },
        pb: { xs: "60px", lg: "80px" },
        minHeight: { xs: '50vh', md: '70vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        overflow: 'visible',
        backgroundImage: `url("/assets/awards_bg.png")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                color: '#F4E4BC',
                mx: 'auto'
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 600,
                color: '#F4E4BC',
                mx: 'auto'
              }}
            >
              {t('hero.description')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* How to Enter Section */}
      <Box id="how-to-enter" sx={{ py: { xs: 6, md: 6 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('steps.overline')}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('steps.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
                {t('steps.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Step-by-Step Procedure */}
          <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative' }}>
            {/* Clean Connecting Line */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                left: 30,
                top: 30,
                bottom: 'calc(30px + 60px)',
                width: 2,
                background: 'linear-gradient(180deg, #D4A747 0%, #B8901C 100%)',
                borderRadius: 1,
                opacity: 0.3
              }}
            />

            {stepsWithIcons.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: { xs: 6, md: 7 },
                    position: 'relative',
                    '&:last-child': { mb: 0 }
                  }}
                >
                  {/* Clean Step Number */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 2,
                      width: { xs: 50, md: 60 },
                      height: { xs: 50, md: 60 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #D4A747 0%, #D4A520 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      mr: { xs: 3, md: 4 },
                      flexShrink: 0,
                      border: '3px solid white',
                      boxShadow: '0 4px 16px rgba(212, 167, 71, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 20px rgba(212, 167, 71, 0.3)'
                      }
                    }}
                  >
                    {step.number}

                    {/* Mobile connecting line */}
                    {index < stepsWithIcons.length - 1 && (
                      <Box
                        sx={{
                          display: { xs: 'block', md: 'none' },
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 2,
                          height: 50,
                          background: '#D4A747',
                          opacity: 0.3
                        }}
                      />
                    )}
                  </Box>

                  {/* Step Content */}
                  <Box
                    sx={{
                      flex: 1,
                      pt: 0
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      <Box sx={{ p: 2.5, mt: -1, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '8px',
                              background: 'rgba(212, 167, 71, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#D4A747',
                              mr: 1.5,
                              flexShrink: 0
                            }}
                          >
                            <step.icon size={18} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#B8901C',
                                mb: 0.5,
                                lineHeight: 1.3,
                                fontSize: '1.1rem'
                              }}
                            >
                              {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                fontSize: '0.8rem'
                              }}
                            >
                              {step.details}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>

        </Container>
      </Box>

      {/* Bhoomee Awards 2026 Coming Soon */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#F5F5F5', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative dots */}
        <Box sx={{ position: 'absolute', top: 0, right: 0, width: 300, height: 300, opacity: 0.08, background: 'radial-gradient(circle, #D4A747 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 5, textTransform: 'uppercase', color: '#D4A747', mb: 2 }}>
                {t('comingSoon.overline')}
              </Typography>
              <Typography sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, fontWeight: 900, background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2, lineHeight: 1.1 }}>
                {t('comingSoon.title')}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.9rem', md: '1.05rem' }, color: 'rgba(0, 0, 0, 0.55)', maxWidth: 600, mx: 'auto', lineHeight: 1.7, mb: 5 }}>
                {t('comingSoon.description')}
              </Typography>

              {/* Three category cards */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, justifyContent: 'center' }}>
                {[
                  { icon: Wheat, key: 'farmer' },
                  { icon: Users, key: 'fpo' },
                  { icon: Rocket, key: 'innovator' },
                ].map((cat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.15 }}
                    viewport={{ once: true }}
                    style={{ flex: 1 }}
                  >
                    <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: '16px', background: '#FFFFFF', border: '1px solid rgba(212, 167, 71, 0.2)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'all 0.3s ease', '&:hover': { borderColor: 'rgba(212, 167, 71, 0.4)', boxShadow: '0 8px 24px rgba(212, 167, 71, 0.1)', transform: 'translateY(-4px)' } }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.12), rgba(184, 144, 28, 0.08))', border: '1px solid rgba(212, 167, 71, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <cat.icon size={24} color="#D4A747" />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1A1A1A', mb: 1 }}>
                        {t(`comingSoon.${cat.key}.title`)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.5)', lineHeight: 1.5 }}>
                        {t(`comingSoon.${cat.key}.description`)}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Nominations;
